import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/lib/toast';
import { supabase } from '@/integrations/supabase/client';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { claimEarlyRole } from '@/hooks/useEarlyRole';

export default function AuthCallback() {
	const navigate = useNavigate();

	useEffect(() => {
		const run = async () => {
			try {
				const hashParams = new URLSearchParams(window.location.hash.substring(1));
				const error = hashParams.get('error');
				const errorDescription = hashParams.get('error_description');
				const type = hashParams.get('type');

				if (error) {
					toast.error(
						error === 'access_denied'
							? 'Email link is invalid or has expired. Please request a new one.'
							: errorDescription || 'Authentication failed. Please try again.',
					);
					window.history.replaceState({}, document.title, '/');
					navigate('/');
					return;
				}

				if (type === 'recovery') {
					window.history.replaceState({}, document.title, '/reset-password');
					navigate('/reset-password');
					return;
				}

				const url = new URL(window.location.href);
				const intentRole = (url.searchParams.get('role') ?? '') as 'buyer' | 'seller' | '';

				// supabase-js parses the URL hash asynchronously when detectSessionInUrl is on.
				// Poll + listen so we don't race the hash parser and flash a false "Sign in failed".
				const waitForSession = () =>
					new Promise<Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']>((resolve) => {
						let settled = false;
						const finish = (s: any) => {
							if (settled) return;
							settled = true;
							sub?.data.subscription.unsubscribe();
							clearInterval(poll);
							clearTimeout(timeout);
							resolve(s ?? null);
						};

						const sub = supabase.auth.onAuthStateChange((event, session) => {
							if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
								finish(session);
							}
						});

						const poll = setInterval(async () => {
							const { data } = await supabase.auth.getSession();
							if (data.session) finish(data.session);
						}, 150);

						const timeout = setTimeout(() => finish(null), 8000);

						supabase.auth.getSession().then(({ data }) => {
							if (data.session) finish(data.session);
						});
					});

				const session = await waitForSession();

				window.history.replaceState({}, document.title, window.location.pathname);

				if (!session) {
					toast.error('Sign in failed. Please try again.');
					navigate('/');
					return;
				}

				let role = null;
				try {
					role = await claimEarlyRole(intentRole || undefined);
				} catch (e) {
					console.error('[AuthCallback] claim-early-role failed', e);
				}

				if (role === 'early_seller' || role === 'admin' || role === 'moderator') {
					navigate('/inventory', { replace: true });
				} else {
					navigate('/', { replace: true });
				}
			} catch (e) {
				console.error('[AuthCallback]', e);
				navigate('/');
			}
		};
		run();
	}, [navigate]);

	return (
		<div className="min-h-screen-safe flex items-center justify-center">
			<LoadingSpinner variant="panel" title="Signing you in" subtitle="" />
		</div>
	);
}
